/**
 * BIND zone-file (RFC 1035) serialiser for the customer-portal Mail
 * domains export flow (OCP-197). Produces a textual representation
 * that imports cleanly into every major DNS provider's bulk-import
 * tool (Cloudflare, Route 53, GoDaddy, Namecheap, DNSimple).
 *
 * Why frontend-only: the data already arrived as `DomainDetail` for
 * the records modal render; doing the textual transform client-side
 * keeps the change contained in one app and avoids a new portal route
 * shape. If we ever need a `GET /v1/portal/mail/domains/:id.zone`
 * endpoint for curl-piping, the same algorithm can move server-side.
 */

export interface DnsRecordLite {
  type: string;
  name: string;
  value: string;
}

export interface BuildZoneFileInput {
  domain: string;
  records: DnsRecordLite[];
  /** ISO-8601 timestamp embedded in the header comment. */
  generatedAt?: string;
}

/**
 * Render an RFC-1035 zone file for the given domain + records.
 *
 * Rules:
 * - $ORIGIN is the FQDN with trailing dot
 * - Names get relative-ised: a record on the apex becomes `@`,
 *   anything else has the trailing `.<domain>` stripped
 * - CNAME values are fully-qualified (trailing `.`)
 * - TXT values are double-quoted; embedded `"` are escaped to `\"`
 *
 * Note: very long TXT values (> 255 chars per the RFC) would normally
 * be split into multiple quoted strings on the same record. Our DKIM
 * CNAMEs are short and our SPF/DMARC TXTs fit in one string, so we
 * skip the split logic for now. If we ever issue a giant TXT
 * (e.g. multi-include SPF), revisit.
 */
export function buildZoneFile(input: BuildZoneFileInput): string {
  const domain = input.domain.replace(/\.+$/, '');
  const origin = `${domain}.`;
  const generatedAt = input.generatedAt ?? new Date().toISOString();

  const lines: string[] = [
    `; orboto Mail DNS records for ${domain}`,
    `; Generated ${generatedAt} by orboto Mail — paste into your DNS provider's zone-file import`,
    `$ORIGIN ${origin}`,
    '',
  ];

  for (const r of input.records) {
    const type = r.type.toUpperCase();
    const relName = relativiseName(r.name, domain);
    const value = formatValue(type, r.value);
    lines.push(formatRow(relName, type, value));
  }

  return `${lines.join('\n')}\n`;
}

function relativiseName(name: string, domain: string): string {
  const trimmed = name.replace(/\.+$/, '').toLowerCase();
  const lcDomain = domain.toLowerCase();
  if (trimmed === lcDomain) return '@';
  if (trimmed.endsWith(`.${lcDomain}`)) {
    return trimmed.slice(0, -1 - lcDomain.length);
  }
  // Name doesn't match the domain — already absolute or sub-domain
  // outside the zone; pass through with a trailing dot so the
  // resolver treats it as FQDN.
  return `${trimmed}.`;
}

function formatValue(type: string, raw: string): string {
  const trimmed = raw.trim();
  if (type === 'TXT') {
    const escaped = trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  if (type === 'CNAME' || type === 'MX' || type === 'NS' || type === 'PTR') {
    // FQDN targets get a trailing dot so the resolver doesn't append $ORIGIN.
    return trimmed.endsWith('.') ? trimmed : `${trimmed}.`;
  }
  return trimmed;
}

/**
 * Single-line formatter with column padding so the output reads cleanly
 * when copied. Padding is generous but not pixel-perfect — DNS
 * tooling parses on whitespace, not column position.
 */
function formatRow(name: string, type: string, value: string): string {
  const namePad = name.length < 44 ? name.padEnd(44, ' ') : `${name} `;
  const typePad = type.padEnd(6, ' ');
  return `${namePad} IN  ${typePad} ${value}`;
}
